import React, { useEffect, useState } from 'react';
import { useBuilding } from '../../context/BuildingContext';
import CabinetShell from '../../components/UI/CabinetShell/cabinetShell';
import MapComponent from '../../components/UI/MapComponent/MapComponent';
import axiosInstance from '../../utils/router/axiosInstance';
import { useParams } from 'react-router';
import initializeEmployeesLocation from '../../utils/initializeEmployeesLocation';
import subscribeLivePositions from '../../utils/livePositions';
import { logger } from '../../utils/logger';
// 🔴 Импорт, которого не было (ревью Codex WEB-9, High). Файл map.css лежал в
// репозитории и НЕ подключался ни из одного модуля: правило «полотно считает
// высоту не само, а по остатку колонки» было мёртвым, и карта на десктопе
// оставалась ровно на высоту шапки длиннее экрана — страница прокручивалась
// на 112 px. Поймано геометрией в check-redesign.mjs, а не чтением каскада.
import './map.css';

// RT-3: realtime-тракт (SSE + деградация в батч-polling) за build-флагом.
// false (дефолт) = легаси-поведение байт-в-байт (N+1 каждые 5с). Включать
// вместе с бэкендными REALTIME_ENABLED + HISTORY_READ_UNIFIED (ранбук).
const REALTIME_ENABLED = process.env.REACT_APP_REALTIME === 'true';

// WEB-3: Leaflet-редактор плана за build-флагом. false (дефолт) = легаси
// MapComponent байт-в-байт. React.lazy: с выключенным флагом чанк
// leaflet/react-leaflet/pdfjs в браузер не грузится вовсе.
const PLAN_EDITOR_ENABLED = process.env.REACT_APP_PLAN_EDITOR === 'true';
const PlanEditor = React.lazy(() => import('../../components/UI/PlanEditor/PlanEditor'));

const Map = () => {
  const { selectedFloor } = useBuilding();
  const [employees, setEmployees] = useState({
    dots: [],
    users: []
  });
  const [selectedBuildingId, setSelectedBuildingId] = useState(localStorage.getItem('selectedBuildingId'));
  const { id: building_id } = useParams();

  useEffect(() => {
    if (!selectedFloor) return undefined;
    // WEB-8: cancelled-гард (как у users ниже) — поздний ответ floors/dots
    // СТАРОГО этажа/здания не должен перетирать слой точек нового.
    let cancelled = false;

    const fetchDots = async () => {
      try {
        const floors = await axiosInstance.get(`website/floors/${building_id}`).then((res) => res.data.floors);
        // WEB-8: в новом здании может не быть текущего этажа — раньше
        // .floor_id кидал TypeError (unhandled rejection, точки не грузились
        // вовсе); теперь честный пустой слой.
        const floorID = floors?.find((floor) => floor.floor === selectedFloor)?.floor_id;
        if (cancelled) return;
        if (!floorID) {
          setEmployees((prev) => ({ dots: [], users: prev.users }));
          return;
        }

        const { data: dots } = await axiosInstance.get(`website/dots?building_id=${building_id}&floor_id=${floorID}`);
        if (cancelled) return;
        // WEB-18: имя точки едет до карты. Раньше здесь оставались только id и
        // координаты, и на плане было пять неотличимых кружков — какой из них
        // какой роутер, узнать было негде, кроме страницы «Wi-Fi точки».
        const filteredDots = dots.map((dot) => ({
          id: dot.id ?? dot.bssid ?? `${dot.x}-${dot.y}`,
          ssid: dot.ssid,
          bssid: dot.bssid,
          coordinates: {
            x: dot.x,
            y: dot.y,
            radius: 50
          }
        }));

        setEmployees((prev) => ({
          dots: filteredDots,
          users: prev.users
        }));
      } catch (error) {
        // сбой сети/500: не оставлять на карте точки ЧУЖОГО этажа
        if (!cancelled) setEmployees((prev) => ({ dots: [], users: prev.users }));
        logger.error('Error fetching dots:', error);
      }
    };

    fetchDots();
    return () => {
      cancelled = true;
    };
  }, [building_id, selectedFloor]);

  useEffect(() => {
    // RT-3: realtime-ветка — один SSE-поток (или 1 батч-запрос/тик при
    // недоступном стриме) вместо N+1; отписка гасит и стрим, и таймеры.
    if (REALTIME_ENABLED) {
      if (!selectedFloor) return undefined;
      setSelectedBuildingId(building_id);
      return subscribeLivePositions({
        buildingId: building_id,
        floor: selectedFloor,
        onUsers: (users) =>
          setEmployees((prev) => ({ dots: prev.dots, users })),
      });
    }

    // Гард от stale-ответа: при быстрой смене здания/этажа in-flight запрос
    // старого этажа завершается позже нового и перезаписал бы метки чужим этажом.
    let cancelled = false;

    const fetchEmployees = async () => {
      setSelectedBuildingId(building_id);
      const locations = await initializeEmployeesLocation(Math.floor(Date.now() / 1000), building_id, selectedFloor);
      if (cancelled) return;

      const employeesWithCoordinates = locations.map(({ user_id, x, y, r }, index) => ({
        id: user_id,
        coordinates: {
          x,
          y,
          radius: r,
        },
      }));

      setEmployees((prev) => ({
        dots: prev.dots,
        users: employeesWithCoordinates,
      }));
    };

    // сразу, не через 5с — иначе после переключения этажа висят метки старого
    fetchEmployees();
    const intervalId = setInterval(fetchEmployees, 5000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [building_id, selectedFloor]);

  // ТЕСТ ТОЧЕК НА КАРТЕ
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const users =
  //       employees.users.length > 0
  //         ? employees.users
  //         : [
  //             {
  //               id: 1,
  //               coordinates: {
  //                 x: 10,
  //                 y: 10,
  //                 radius: 50
  //               }
  //             }
  //           ];
  //     setEmployees((prev) => ({
  //       ...prev,
  //       users: users.map((user) => ({
  //         ...user,
  //         coordinates: {
  //           ...user.coordinates,
  //           x: user.coordinates.x + (Math.random() * 10 - 10),
  //           y: user.coordinates.y + (Math.random() * 20 - 10)
  //         }
  //       }))
  //     }));
  //   }, 5000);

  //   return () => clearInterval(interval);
  // }, []);

  return (
    <CabinetShell title="Карта" subtitle={selectedFloor ? `Этаж ${selectedFloor}` : undefined}>
      {PLAN_EDITOR_ENABLED ? (
        <React.Suspense
          fallback={<div className="mapContainer" data-testid="plan-editor-loading" />}
        >
          <PlanEditor employees={employees} building_id={selectedBuildingId} />
        </React.Suspense>
      ) : (
        <MapComponent employees={employees} selectedBuilding={selectedFloor} building_id={selectedBuildingId} />
      )}
    </CabinetShell>
  );
};

export default Map;
